import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import DataTableExtensions from "react-data-table-component-extensions";
import "react-data-table-component-extensions/dist/index.css";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Link from "@mui/material/Link";
import { useSearchParams } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import useStyles from "../dashAuction.style";
import AuctionLotsRow from "./AuctionLotsRow";
import eventListAPI from "../../../../api/adminAPI/eventListAPI";
import eventListBySellerAPI from "../../../../api/adminAPI/eventListBySellerAPI";
import eventDeleteAPI from "../../../../api/adminAPI/eventDeleteAPI";
import getEventItemsListAPIAdmin from "../../../../api/getEventItemsListAPIAdmin";
import getUserProfileAPI from "../../../../api/getUserProfileAPI";
// import { columns, data } from "./Data.js";
import lodash from "lodash";
import moment from "moment";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getTimeZone, getUserData, localTimeZone } from "../../../../common";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import { FaRegFileExcel } from "react-icons/fa";
import * as XLSX from "xlsx";
import { DATE_TIME_DISPLAY, dateSortValue } from "../../../../common/dateTime";

const AUCTION_STATUSES = ["live", "upcoming", "past", "incomplete"];

// same rules as the dashboard Live / Upcoming / Past counts
const getAuctionTimeStatus = (data, now) => {
  if (moment(data.bidStartDateTime).isAfter(now)) return "upcoming";

  if (data.eventFormat === "ENGLISH_AUCTION") {
    // no end date yet (not set up fully) - not counted as live or past
    if (!data.bidEndDateTime) return "incomplete";
    return moment(data.bidEndDateTime).isBefore(now) ? "past" : "live";
  }

  // Dutch / Japanese: over once every lot is concluded
  return data.openLots > 0 ? "live" : "past";
};

function AuctionTable({ onView, onEdit, buyerId }) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [eventId, setEventId] = useState("");
  const [tableEventsData, setTableEventsData] = useState([]);
  const [popoverDetails, setPopoverDetails] = useState(null);
  const [startDateFrom, setStartDateFrom] = useState("");
  const [startDateTo, setStartDateTo] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get("status");
  const statusFilter = AUCTION_STATUSES.includes(statusParam)
    ? statusParam
    : "";
  const [exportReportType, setExportReportType] = useState("AUCTIONS");
  const [isExporting, setIsExporting] = useState(false);
  // eventId -> true for every auction whose lots are expanded
  const [expandedRows, setExpandedRows] = useState({});
  // eventId -> { apiState, list } - lots are fetched once and kept around so
  // collapsing and expanding a row again does not hit the API
  const [lotsByEvent, setLotsByEvent] = useState({});
  const lotsRequestedRef = useRef({});
  // deleting an auction is admin-only, auctioneers never get the delete action
  const isAdmin = (getUserData() ?? {}).role === "ADMIN";

  useEffect(() => {
    eventsList();
  }, []);

  const getZone = getTimeZone() ? getTimeZone() : localTimeZone();

  const handleClick = (event, eventId, id) => {
    // the row itself toggles the lots, the action menu should not
    event.stopPropagation();
    setPopoverDetails(tableEventsData[id - 1]);
    setEventId(eventId);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setEventId("");
    setPopoverDetails(null);
  };

  const getEventFormatText = (eventFormat) => {
    switch (eventFormat) {
      case "DUTCH_AUCTION":
        return "Dutch";
      case "JAPANESE_AUCTION":
        return "Japanese";
      default:
        return "English";
    }
  };

  const eventsList = async () => {
    try {
      let dataEvent = "";
      let auctionGracePeriodHours = 72;
      if (buyerId === undefined) {
        dataEvent = await eventListAPI();

        // fetch seller details - we need auctionGracePeriodHours to decide auction end date
        // and if auction should be edited or not
        const sellerDetails = await getUserProfileAPI();
        const gracePeriod = sellerDetails?.data?.auctionGracePeriodHours;

        if (typeof gracePeriod === "number" && gracePeriod >= 0)
          auctionGracePeriodHours = gracePeriod;
      } else {
        const payload = { userId: buyerId };
        dataEvent = await eventListBySellerAPI(payload);
      }

      if (dataEvent) {
        const currentDate = moment();
        let id = 1;
        const eventsBuyerList = dataEvent.data.map((data, _index) => {
          const auctionEndDate = moment(data.bidEndDateTime).add(
            auctionGracePeriodHours,
            "hours"
          ); // auction end date + <auctionGracePeriodHours> hours
          const auctionEnded = auctionEndDate.isBefore(currentDate);

          return {
            id: id++,
            auctioneerName: data.auctioneerName,
            auctionStatus: data.auctionStatus,
            eventFormatRaw: data.eventFormat,
            eventId: data.eventId,
            eventNumber: data.eventNumber,
            auctionTitle: data.auctionTitle,
            eventType:
              data.eventType === "FORWARD_AUCTION" ? "FORWARD" : "REVERSE",
            eventFormat: getEventFormatText(data.eventFormat) || "ENGLISH",
            totalLots: data.totalLots,
            totalBuyers: data.totalBuyers,
            totalAllotedLots: data.totalAllotedLots,
            totalBids: data.totalBids,
            ownerOfLots: data.ownerOfLots,
            bidsCount: data.bidsCount,
            location: `${data.country} ${data.province}`,
            bidStartAt: data.bidStartDateTime,
            bidEndAt:
              data.eventFormat === "ENGLISH_AUCTION" ? data.bidEndDateTime : null,
            bidStartDateTime: moment(data.bidStartDateTime)
              .tz(getZone)
              .format(DATE_TIME_DISPLAY),
            // calendar date (in the viewer's timezone) used by the start date filter
            bidStartDate: moment(data.bidStartDateTime)
              .tz(getZone)
              .format("YYYY-MM-DD"),
            bidEndDateTime:
              data.eventFormat === "ENGLISH_AUCTION"
                ? moment(data.bidEndDateTime)
                    .tz(getZone)
                    .format(DATE_TIME_DISPLAY)
                : "-",
            auctionEnded,
            timeStatus: getAuctionTimeStatus(data, currentDate),
            canEdit:
              data.eventFormat === "ENGLISH_AUCTION"
                ? !auctionEnded
                : data.auctionStatus !== "live",
          };
        });

        setTableEventsData(eventsBuyerList);
      } else throw new Error(dataEvent);
    } catch (error) {
      console.log(error);
    }
  };

  // filter on live / upcoming / past and on the auction start date - both
  // date bounds are inclusive and optional
  const filteredEventsData = useMemo(() => {
    if (!statusFilter && !startDateFrom && !startDateTo) return tableEventsData;

    return tableEventsData.filter(({ bidStartDate, timeStatus }) => {
      if (statusFilter && timeStatus !== statusFilter) return false;
      if (!startDateFrom && !startDateTo) return true;
      if (!bidStartDate) return false;
      if (startDateFrom && bidStartDate < startDateFrom) return false;
      if (startDateTo && bidStartDate > startDateTo) return false;
      return true;
    });
  }, [tableEventsData, statusFilter, startDateFrom, startDateTo]);

  // keep the status in the URL so the dashboard cards can link straight here
  const changeStatusFilter = (status) => {
    const params = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });

    if (status) params.status = status;
    else delete params.status;

    setSearchParams(params);
  };

  const clearStartDateFilter = () => {
    setStartDateFrom("");
    setStartDateTo("");
  };

  // fetches the lots of a single auction. only called by the expanded rows
  // that are actually rendered, so paging through an expanded table loads the
  // lots of the visible auctions only
  const fetchLots = useCallback(async (auctionEventId, force = false) => {
    if (!auctionEventId) return;
    if (!force && lotsRequestedRef.current[auctionEventId]) return;

    lotsRequestedRef.current[auctionEventId] = true;
    setLotsByEvent((previous) => ({
      ...previous,
      [auctionEventId]: { apiState: "loading", list: [] },
    }));

    try {
      const { data } = await getEventItemsListAPIAdmin({
        itemId: auctionEventId,
      });

      setLotsByEvent((previous) => ({
        ...previous,
        [auctionEventId]: { apiState: "success", list: data || [] },
      }));
    } catch (error) {
      console.log(error);
      // let the row retry the call
      lotsRequestedRef.current[auctionEventId] = false;
      setLotsByEvent((previous) => ({
        ...previous,
        [auctionEventId]: { apiState: "error", list: [] },
      }));
    }
  }, []);

  const handleRowExpandToggled = useCallback((expanded, row) => {
    setExpandedRows((previous) => {
      if (expanded) return { ...previous, [row.eventId]: true };

      return lodash.omit(previous, row.eventId);
    });
  }, []);

  const allRowsExpanded =
    !lodash.isEmpty(filteredEventsData) &&
    filteredEventsData.every(({ eventId: rowEventId }) =>
      Boolean(expandedRows[rowEventId])
    );

  const toggleExpandAll = () => {
    if (allRowsExpanded) {
      setExpandedRows({});
      return;
    }

    setExpandedRows(
      filteredEventsData.reduce(
        (expanded, { eventId: rowEventId }) => ({
          ...expanded,
          [rowEventId]: true,
        }),
        {}
      )
    );
  };

  const expandableRowsProps = useMemo(
    () => ({ lotsByEvent, fetchLots, getZone }),
    [lotsByEvent, fetchLots, getZone]
  );

  const open = Boolean(anchorEl);
  const columnsEventList = [
    {
      name: "Sr.",
      selector: (row) => row.id,
      sortable: true,
    },
    {
      name: "Seller",
      selector: (row) => row.auctioneerName,
      cell: (row) => (
        <Tooltip
          title={row.auctioneerName}
          placement="top-start"
          TransitionComponent={Zoom}
        >
          <span data-tag="allowRowEvents" className={classes.ellipsisText}>
            {row.auctioneerName}
          </span>
        </Tooltip>
      ),
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row.auctionStatus,
      sortable: true,
    },
    {
      name: "Auction ID",
      selector: (row) => row.eventNumber,
      sortable: true,
    },
    {
      name: "Event Name",
      selector: (row) => row.auctionTitle,
      cell: (row) => (
        <Tooltip
          title={row.auctionTitle}
          placement="top-start"
          TransitionComponent={Zoom}
        >
          <span data-tag="allowRowEvents" className={classes.ellipsisText}>
            {row.auctionTitle}
          </span>
        </Tooltip>
      ),
      sortable: true,
    },
    {
      name: "Event Format",
      selector: (row) => row.eventFormat,
      sortable: true,
    },
    {
      name: "Event Type",
      selector: (row) => row.eventType,
      sortable: true,
    },
    {
      name: "Start Date & Time",
      selector: (row) => dateSortValue(row.bidStartAt),
      cell: (row) => row.bidStartDateTime,
      sortable: true,
    },
    {
      name: "End Date & Time",
      selector: (row) => dateSortValue(row.bidEndAt),
      cell: (row) => row.bidEndDateTime || "-",
      sortable: true,
    },
    {
      name: "No. of Bids",
      selector: (row) => row.bidsCount,
      sortable: true,
    },
    {
      name: "No. of Lots",
      selector: (row) => row.totalLots,
      sortable: true,
    },
    {
      name: "Lot Owner",
      selector: (row) => row.ownerOfLots,
      cell: (row) => (
        <Tooltip
          title={row.ownerOfLots}
          placement="top-start"
          TransitionComponent={Zoom}
        >
          <span data-tag="allowRowEvents" className={classes.ellipsisText}>
            {row.ownerOfLots}
          </span>
        </Tooltip>
      ),
      sortable: true,
    },
    {
      name: "No. of Buyers",
      selector: (row) => row.totalBuyers,
      sortable: true,
    },
    {
      name: "Alloted Lots",
      selector: (row) => row.totalAllotedLots,
      sortable: true,
    },
    {
      name: "Bidded Lots",
      selector: (row) => row.totalBids,
      sortable: true,
    },
    {
      name: "Location",
      selector: (row) => row.location,
      sortable: true,
    },
    {
      name: "Action",
      // keeps a click on the action menu from also expanding the row
      ignoreRowClick: true,
      cell: (row) => (
        <Button
          variant="contained"
          onClick={(event) => handleClick(event, row.eventId, row.id)}
        >
          <MoreVertIcon />
        </Button>
      ),
    },
  ];

  const [openDilogue, setOpenDilogue] = React.useState(false);

  const [deleteEventId, setDeleteEventId] = React.useState("");

  const updateDeleteEvent = async () => {
    try {
      const { data } = await eventDeleteAPI(deleteEventId);

      toast("Event Deleted Successfully!");
      setTimeout(() => {
        eventsList();
        setOpenDilogue(false);
        setAnchorEl(null);
      }, 1000);
    } catch (error) {
      toast.error("Live event cannot be deleted");
      console.log(error);
    }
  };

  const handleClickOpen = (eventId) => {
    setOpenDilogue(true);
    setDeleteEventId(eventId);
  };

  const handleCloseDilogue = () => {
    setOpenDilogue(false);
    setAnchorEl(null);
  };

  const writeXLSX = (headerColumns, dataColumns, fileName) => {
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet([headerColumns, ...dataColumns]);

    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, fileName);
  };

  const exportToXLSX = (tableData, tableHeader, fileName) => {
    const headerColumns = lodash
      .filter(tableHeader, ({ name }) => name !== "Action")
      .map(({ name }) => name);
    const dataColumns = lodash.map(tableData, (columns) => [
      columns.id,
      columns.auctioneerName,
      columns.auctionStatus,
      columns.eventNumber,
      columns.auctionTitle,
      columns.eventFormat,
      columns.eventType,
      columns.bidStartDateTime,
      columns.bidEndDateTime,
      columns.bidsCount,
      columns.totalLots,
      columns.ownerOfLots,
      columns.totalBuyers,
      columns.totalAllotedLots,
      columns.totalBids,
      columns.location,
    ]);

    writeXLSX(headerColumns, dataColumns, fileName);
  };

  const formatLotDateTime = (value) =>
    value ? moment(value).tz(getZone).format(DATE_TIME_DISPLAY) : "-";

  // one row per lot, repeating the auction details for every lot it holds.
  // auctions without lots are still listed, with the lot columns left blank
  const exportAuctionsWithLotsToXLSX = async (tableData, fileName) => {
    const headerColumns = [
      "Sr.",
      "Seller",
      "Status",
      "Auction ID",
      "Event Name",
      "Event Format",
      "Event Type",
      "Auction Start Date & Time",
      "Auction End Date & Time",
      "Location",
      "Lot No",
      "Item No",
      "Short Description",
      "Qty",
      "Unit",
      "Currency",
      "Start Price",
      "Increment/Decrement",
      "Reserve Price",
      "Current Price",
      "Lot Start Date & Time",
      "Lot End Date & Time",
      "Concluded",
    ];

    // fetch the lots in small batches so a wide date range does not fire
    // one request per auction all at once
    const lotsByEvent = {};
    for (const auctions of lodash.chunk(tableData, 5)) {
      await Promise.all(
        auctions.map(async ({ eventId: auctionEventId }) => {
          const { data } = await getEventItemsListAPIAdmin({
            itemId: auctionEventId,
          });
          lotsByEvent[auctionEventId] = data || [];
        })
      );
    }

    const dataColumns = lodash.flatMap(tableData, (auction) => {
      const auctionColumns = [
        auction.id,
        auction.auctioneerName,
        auction.auctionStatus,
        auction.eventNumber,
        auction.auctionTitle,
        auction.eventFormat,
        auction.eventType,
        auction.bidStartDateTime,
        auction.bidEndDateTime,
        auction.location,
      ];
      const lots = lotsByEvent[auction.eventId] || [];

      if (lodash.isEmpty(lots)) return [[...auctionColumns]];

      return lots.map((lot) => [
        ...auctionColumns,
        lot.lotNumber,
        lot.itemNumber,
        lot.shortDescription,
        lot.quantity,
        lot.unit,
        lot.currency,
        lot.startPrice,
        lot.increment,
        lot.reservePrice,
        lot.currentPrice,
        formatLotDateTime(lot.startDateAndTime),
        formatLotDateTime(lot.endDateAndTime),
        lot.concluded,
      ]);
    });

    writeXLSX(headerColumns, dataColumns, fileName);
  };

  const handleExportReport = async () => {
    // the reports always cover a start date range, so both bounds are required
    if (!startDateFrom || !startDateTo) {
      toast.error(
        "Please select a start date range (From and To) to export the report."
      );
      return;
    }

    if (lodash.isEmpty(filteredEventsData)) {
      toast.error("No auctions found for the selected start date range.");
      return;
    }

    const fileSuffix = `${startDateFrom}-to-${startDateTo}`;

    try {
      setIsExporting(true);

      if (exportReportType === "AUCTIONS_WITH_LOTS") {
        await exportAuctionsWithLotsToXLSX(
          filteredEventsData,
          `auctions-with-lots-${fileSuffix}.xlsx`
        );
      } else {
        exportToXLSX(
          filteredEventsData,
          columnsEventList,
          `auctions-listing-${fileSuffix}.xlsx`
        );
      }
    } catch (error) {
      console.log(error);
      toast.error("Report could not be exported, please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="AuctionTable" source="ADMIN||SELLER">
      <h3>Auctions Listing</h3>
      <Popover
        id={`${popoverDetails?.eventId}`}
        key={`${popoverDetails?.eventId}`}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Typography sx={{ p: 2, display: "grid" }}>
          <div
            className={classes.viewBtn}
            onClick={() => {
              onView(popoverDetails?.eventId);
            }}
          >
            <RemoveRedEyeIcon
              style={{ color: "#2D55EB", paddingRight: "5px" }}
            />
            Report
          </div>
          <span
            className={classes.viewBtn}
            onClick={() => {
              popoverDetails?.canEdit && onEdit(eventId);
            }}
            style={{
              color: popoverDetails?.canEdit ? "" : "grey",
            }}
          >
            <EditIcon
              style={{
                color: popoverDetails?.canEdit ? "#2D55EB" : "grey",
                paddingRight: "5px",
              }}
            />
            Edit
          </span>
          {isAdmin && (
            <span
              className={classes.viewBtn}
              onClick={() => handleClickOpen(eventId)}
            >
              <DeleteIcon style={{ color: "red", paddingRight: "5px" }} />
              Delete
            </span>
          )}
        </Typography>
      </Popover>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <TextField
          select
          size="small"
          label="Status"
          sx={{ minWidth: 140 }}
          value={statusFilter || "all"}
          onChange={(event) =>
            changeStatusFilter(
              event.target.value === "all" ? "" : event.target.value
            )
          }
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="live">Live</MenuItem>
          <MenuItem value="upcoming">Upcoming</MenuItem>
          <MenuItem value="past">Past</MenuItem>
          <MenuItem value="incomplete">Incomplete (no end date)</MenuItem>
        </TextField>
        <TextField
          type="date"
          size="small"
          label="Start Date From"
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: startDateTo || undefined }}
          value={startDateFrom}
          onChange={(event) => setStartDateFrom(event.target.value)}
        />
        <TextField
          type="date"
          size="small"
          label="Start Date To"
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: startDateFrom || undefined }}
          value={startDateTo}
          onChange={(event) => setStartDateTo(event.target.value)}
        />
        {(startDateFrom || startDateTo) && (
          <Button variant="outlined" onClick={clearStartDateFilter}>
            Clear
          </Button>
        )}

        <TextField
          select
          size="small"
          label="Export Report"
          sx={{ minWidth: 220, ml: { md: "auto" } }}
          value={exportReportType}
          onChange={(event) => setExportReportType(event.target.value)}
        >
          <MenuItem value="AUCTIONS">Auctions</MenuItem>
          <MenuItem value="AUCTIONS_WITH_LOTS">Auctions with Lots</MenuItem>
        </TextField>
        <Button
          variant="contained"
          onClick={handleExportReport}
          disabled={isExporting}
          startIcon={
            <FaRegFileExcel style={{ fontSize: "20px", color: "#04aa18" }} />
          }
        >
          {isExporting ? "Exporting..." : "Export"}
        </Button>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Link
          component="button"
          type="button"
          underline="none"
          className={classes.expandAllLink}
          onClick={toggleExpandAll}
          disabled={lodash.isEmpty(filteredEventsData)}
        >
          {allRowsExpanded ? (
            <UnfoldLessIcon fontSize="small" />
          ) : (
            <UnfoldMoreIcon fontSize="small" />
          )}
          {allRowsExpanded ? "Collapse all lots" : "Expand all lots"}
        </Link>
      </Box>

      <DataTableExtensions
        print={false}
        export={false}
        exportHeaders={false}
        columns={columnsEventList}
        data={filteredEventsData}
      >
        <DataTable
          noHeader={false}
          defaultSortField="id"
          defaultSortAsc={false}
          pagination
          highlightOnHover
          pointerOnHover
          className="DataTable-icon"
          keyField="eventId"
          columns={columnsEventList}
          data={filteredEventsData}
          expandableRows
          expandOnRowClicked
          expandableRowExpanded={(row) => Boolean(expandedRows[row.eventId])}
          onRowExpandToggled={handleRowExpandToggled}
          expandableRowsComponent={AuctionLotsRow}
          expandableRowsComponentProps={expandableRowsProps}
        />
      </DataTableExtensions>

      <Dialog
        open={openDilogue}
        onClose={handleCloseDilogue}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Delete Event ?"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete Event ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDilogue}>Cancle</Button>
          <Button onClick={updateDeleteEvent} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default AuctionTable;
